import _ from "lodash"
import {basicContext} from "../../../utils/contextUtils"

export const entities = [
    {
        name: 'LiberalityAdvance',
        facets: ['conversations'],
        fields: [
            {path: 'beneficiary', type: 'Organization', unique: true},
            {path: 'solicitations', type: 'LiberalitySolicitation', link: 'OTM'},
            {
                path: 'imputations',
                fieldPath: ['beneficiary.id'],
                $f: function (advance, context, callback) {
                    if (!advance.beneficiary) return callback(null, [])
                    const beneficiaryId = new global.ObjectID(advance.beneficiary.id)
                    Promise.all([
                        global.app.C.LiberalityReversionDeparture.find({
                            ...basicContext(context),
                            query: {beneficiary: beneficiaryId, departureForm: 'advanceRecovery'},
                            fieldPath: [
                                'id',
                                'date',
                                'advanceRecoveryAmount',
                                'comment',
                                'asset.identifier',
                                'asset.applicationNumber',
                                'asset.typeOfFile',
                                'asset.liberalityRecord.presentationDate',
                                'asset.liberalityRecord.decisionDate',
                                'asset.liberalityRecord.benefactor',
                                'asset.liberalityRecord.status.id', 'asset.liberalityRecord.status.name', 'asset.liberalityRecord.status.bStyle',
                                'asset.liberalityRecord.administrator.fullName',
                            ]
                        }),
                        global.app.C.AssetPartition.find({
                            ...basicContext(context),
                            query: {beneficiary: beneficiaryId},
                            fieldPath: ['id', 'assetInfos.id', 'qualification.id', 'qualification.name']
                        })
                    ]).then(([departures, partitions]) => {
                        const qualificationByAssetId = _.fromPairs(
                            partitions.map(partition => [partition.assetInfos?.id, _.pick(partition.qualification, ['id', 'name'])])
                        )
                        callback(null, departures.map(departure => ({
                            id: departure.id,
                            date: departure.date,
                            advanceRecoveryAmount: departure.advanceRecoveryAmount,
                            comment: departure.comment,
                            qualification: qualificationByAssetId[departure.asset?.id],
                            asset: {
                                ..._.pick(departure.asset, ['id', 'identifier', 'applicationNumber', 'typeOfFile']),
                                liberalityRecord: {
                                    presentationDate: departure.asset?.liberalityRecord?.presentationDate,
                                    decisionDate: departure.asset?.liberalityRecord?.decisionDate,
                                    benefactor: departure.asset?.liberalityRecord?.benefactor,
                                    status: _.pick(departure.asset?.liberalityRecord?.status, ['id', 'name', 'bStyle']),
                                    administrator: _.pick(departure.asset?.liberalityRecord?.administrator, ['id', 'fullName']),
                                },
                            },
                        })))
                    }).catch(callback)
                }
            },
            {
                path: 'beneficiaryFiles',
                fieldPath: ['beneficiary.id'],
                $f: function (advance, context, callback) {
                    if (!advance.beneficiary) return callback(null, [])
                    global.app.C.AssetPartition.find({
                        ...basicContext(context),
                        query: {beneficiary: new global.ObjectID(advance.beneficiary.id)},
                        fieldPath: [
                            'id',
                            'assetInfos.liberalityRecord.applicationNumber',
                            'assetInfos.liberalityRecord.status.id', 'assetInfos.liberalityRecord.status.name', 'assetInfos.liberalityRecord.status.bStyle',
                            'assetInfos.liberalityRecord.decisionDate',
                            'assetInfos.liberalityRecord.presentationDate',
                            'assetInfos.liberalityRecord.benefactor',
                            'qualification.id', 'qualification.name',
                            'assetInfos.liberalityRecord.administrator',
                            'assetInfos.liberalityRecord.typeOfFile',
                            'assetInfos.identifier',
                            'estimatedBeneficiaryNetValue',
                            'CNDAShareInEstimatedBeneficiaryNetValue',
                            'assetInfos.status.id', 'assetInfos.status.name',
                        ]
                    }).then(list => callback(null, list)).catch(callback)
                }
            },
            {
                path: 'acceptedSolicitationsCount',
                tKey: 'Nombre',
                type: 'integer',
                fieldPath: ['solicitations.status'],
                f: function () {
                    return (this.solicitations || []).filter(solicitation => solicitation.status?.id === 'accepted').length
                }
            },
            {
                path: 'grantedTotal',
                tKey: 'Accordé (€)',
                type: 'decimal',
                fieldPath: ['solicitations.grantedAmount'],
                f: function () {
                    return _.sumBy(this.solicitations || [], 'grantedAmount')
                }
            },
            {
                path: 'paidTotal',
                tKey: 'Versé (€)',
                type: 'decimal',
                fieldPath: ['solicitations.grantedAmount', 'solicitations.paymentDate'],
                f: function () {
                    return _.sumBy((this.solicitations || []).filter(solicitation => solicitation.paymentDate), 'grantedAmount')
                }
            },
            {
                path: 'imputedTotal',
                tKey: 'Imputé (€)',
                type: 'decimal',
                fieldPath: ['imputations.advanceRecoveryAmount'],
                f: function () {
                    return _.sumBy(this.imputations || [], 'advanceRecoveryAmount')
                }
            },
            {
                path: 'balance',
                tKey: 'Solde (€)',
                type: 'decimal',
                fieldPath: ['solicitations.grantedAmount', 'solicitations.paymentDate', 'imputations.advanceRecoveryAmount'],
                f: function () {
                    const paidTotal = _.sumBy((this.solicitations || []).filter(solicitation => solicitation.paymentDate), 'grantedAmount')
                    const imputedTotal = _.sumBy(this.imputations || [], 'advanceRecoveryAmount')
                    return paidTotal - imputedTotal
                }
            },
            {
                path: 'lastPaymentDate',
                tKey: 'Dernier versement le',
                type: 'date',
                nullable: true,
                fieldPath: ['solicitations.paymentDate'],
                f: function () {
                    const paymentDates = (this.solicitations || []).map(solicitation => solicitation.paymentDate).filter(Boolean)
                    return paymentDates.length ? _.max(paymentDates) : null
                }
            },
            {
                path: 'noDeleteButtonAccess',
                fieldPath: ['solicitations.decisionDate', 'solicitations.paymentDate'],
                f: function () {
                    return (this.solicitations || []).some(solicitation => solicitation.decisionDate || solicitation.paymentDate)
                }
            },
            {path: 'updatedBy', type: 'User', nullable: true},
            {path: 'updatedAt', type: 'date', dateFormat: 'YYYY-MM-DD HH:mm', nullable: true},
        ],
        beforeSave: (object, oldObject, context, callback) => {
            object.updatedBy = _.pick(context.user, ['id', 'name'])
            object.updatedAt = new Date()
            callback(null, object, oldObject)
        },
        filters: [
            {
                name: 'byBeneficiary',
                isDefault: false,
                async: true,
                query: async function (context, callback) {
                    const beneficiaryId = _.get(context, "data.beneficiaryId")
                    if (!beneficiaryId) return callback(null, {_id: null})

                    return callback(null, {beneficiary: new global.ObjectID(beneficiaryId)})
                }
            }
        ]
    },
    {
        name: 'LiberalitySolicitation',
        //type: 'mongoInternal',
        fields: [
            {path: 'requestedAmount', type: 'decimal'},
            {path: 'requestDate', type: 'date'},
            {path: 'description', nullable: true},
            {path: 'status', type: 'LiberalitySolicitationStatus'},
            {path: 'decisionBody', type: 'DecisionBody', nullable: true},
            {path: 'decisionDate', type: 'date', nullable: true},
            {path: 'grantedAmount', type: 'decimal', default: 0},
            {path: 'paymentDate', type: 'date', nullable: true},
        ],
    },
]